import type { PageContext } from '../../lib/types';
import { html } from '../../lib/dom';
import { pageHead } from '../../components/ui';

type Row = { id:string; name:string; organization_id?:string; signup_enabled?:boolean; workspace_type?:string };
const esc = (value:unknown) => String(value ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c] as string));
const request = async (path:string, options:RequestInit = {}) => {
  const response = await fetch(path, { credentials:'same-origin', ...options, headers:{ ...(options.body ? {'Content-Type':'application/json'} : {}), ...options.headers } });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || body.message || `Request failed (${response.status})`);
  return body;
};

export function render(_ctx:PageContext) {
  return {
    title:'Admin · Brands & workspaces',
    body:html`<div class="page" data-tenancy>
      ${pageHead({ eyebrow:'Platform admin', title:'Brands & workspaces', lede:'Create brands and configure branded or unbranded workspaces. New users can register into brands enabled for signup.' })}
      <div class="grid c2" style="align-items:start">
        <section class="card pad-lg col" style="gap:14px"><h2>Create brand</h2>
          <label class="field">Organization<select class="select" data-brand-org><option>Loading…</option></select></label>
          <label class="field">Brand name<input class="input" data-brand-name placeholder="e.g. Bots & Bones"></label>
          <button class="btn primary" data-create-brand>Create brand</button>
        </section>
        <section class="card pad-lg col" style="gap:14px"><h2>Create workspace</h2>
          <label class="field">Organization<select class="select" data-workspace-org><option>Loading…</option></select></label>
          <label class="field">Workspace name<input class="input" data-workspace-name placeholder="e.g. Creative Studio"></label>
          <label class="field">Type<select class="select" data-workspace-type><option value="branded">Branded — exactly one brand</option><option value="unbranded">Unbranded — one or more brands</option></select></label>
          <label class="field">Brands<select class="select" data-workspace-brands multiple size="5"></select></label>
          <label class="field">Description<textarea class="textarea" data-workspace-description rows="2"></textarea></label>
          <button class="btn primary" data-create-workspace>Create workspace</button>
        </section>
      </div>
      <div class="banner" data-tenancy-status role="status" style="margin-top:14px">Loading administration data…</div>
    </div>`,
    mount(root:HTMLElement) {
      const orgSelect = root.querySelector<HTMLSelectElement>('[data-brand-org]')!;
      const workspaceOrgSelect = root.querySelector<HTMLSelectElement>('[data-workspace-org]')!;
      const brandsSelect = root.querySelector<HTMLSelectElement>('[data-workspace-brands]')!;
      const status = root.querySelector<HTMLElement>('[data-tenancy-status]')!;
      const refresh = async () => {
        const [orgs, brands] = await Promise.all([
          request('/api/v1/admin/organizations') as Promise<Row[]>,
          request('/api/v1/ui/brands') as Promise<Row[]>,
        ]);
        const options = orgs.map(o => `<option value="${esc(o.id)}">${esc(o.name)}</option>`).join('');
        orgSelect.innerHTML = options;
        workspaceOrgSelect.innerHTML = options;
        const selectedOrg = workspaceOrgSelect.value || orgs[0]?.id;
        workspaceOrgSelect.value = selectedOrg || '';
        const matching = brands.filter(b => b.organization_id === selectedOrg);
        brandsSelect.innerHTML = matching.map(b => `<option value="${esc(b.id)}">${esc(b.name)}</option>`).join('');
        status.className = 'banner info';
        status.textContent = `${orgs.length} organizations · ${brands.length} brands.`;
      };
      workspaceOrgSelect.addEventListener('change', () => { void refresh(); });
      root.querySelector('[data-create-brand]')?.addEventListener('click', async () => {
        const button = root.querySelector<HTMLButtonElement>('[data-create-brand]')!;
        button.disabled = true;
        try {
          await request('/api/v1/admin/brands', { method:'POST', body:JSON.stringify({ organizationId:orgSelect.value, name:root.querySelector<HTMLInputElement>('[data-brand-name]')!.value, signupEnabled:false }) });
          root.querySelector<HTMLInputElement>('[data-brand-name]')!.value = '';
          await refresh();
        } catch (error) { status.className = 'banner error'; status.textContent = String((error as Error).message); }
        finally { button.disabled = false; }
      });
      root.querySelector('[data-create-workspace]')?.addEventListener('click', async () => {
        const button = root.querySelector<HTMLButtonElement>('[data-create-workspace]')!;
        button.disabled = true;
        try {
          await request('/api/v1/admin/workspaces', { method:'POST', body:JSON.stringify({
            organizationId:workspaceOrgSelect.value,
            name:root.querySelector<HTMLInputElement>('[data-workspace-name]')!.value,
            workspaceType:root.querySelector<HTMLSelectElement>('[data-workspace-type]')!.value,
            brandIds:Array.from(brandsSelect.selectedOptions, option => option.value),
            description:root.querySelector<HTMLTextAreaElement>('[data-workspace-description]')!.value,
          }) });
          root.querySelector<HTMLInputElement>('[data-workspace-name]')!.value = '';
          status.className = 'banner info'; status.textContent = 'Workspace created.';
        } catch (error) { status.className = 'banner error'; status.textContent = String((error as Error).message); }
        finally { button.disabled = false; }
      });
      void refresh().catch(error => { status.className = 'banner error'; status.textContent = String(error.message); });
    },
  };
}
